import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import * as requirementService from '../services/requirementService';
import type { RequirementWithDetails } from '../services/requirementService';

export function RequirementMetrics(): React.ReactElement | null {
  const { projectId } = useParams<{ projectId: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [requirements, setRequirements] = useState<RequirementWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter' | 'year'>('year');

  useEffect(() => {
    loadRequirements();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token]);

  const loadRequirements = async () => {
    if (!token || !projectId) return;

    try {
      setIsLoading(true);
      const data = await requirementService.getProjectRequirements(projectId, token);
      setRequirements(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requirements');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate metrics
  const totalRequirements = requirements.length;
  const byStatus = requirements.reduce((acc, req) => {
    acc[req.approval_status] = (acc[req.approval_status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const byPriority = requirements.reduce((acc, req) => {
    acc[req.priority] = (acc[req.priority] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const byType = requirements.reduce((acc, req) => {
    acc[req.requirement_type] = (acc[req.requirement_type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Requirements by user
  const byUser = requirements.reduce((acc, req) => {
    const key = req.created_by_name;
    if (!acc[key]) {
      acc[key] = { count: 0, email: req.created_by_email };
    }
    acc[key].count++;
    return acc;
  }, {} as Record<string, { count: number; email: string }>);

  const userStats = Object.entries(byUser)
    .map(([name, data]) => ({ name, count: data.count, email: data.email }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Top 10 most discussed requirements
  const mostDiscussed = [...requirements]
    .sort((a, b) => {
      const aTotal = (a.comment_count || 0) + (a.reply_count || 0);
      const bTotal = (b.comment_count || 0) + (b.reply_count || 0);
      return bTotal - aTotal;
    })
    .slice(0, 10);

  // Requirements over time
  const today = new Date();

  const getTimelineData = () => {
    if (timeRange === 'week') {
      const data: { label: string; count: number }[] = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dayKey = date.toISOString().split('T')[0];
        const count = requirements.filter(req => {
          const reqDate = new Date(req.created_at);
          return reqDate.toISOString().split('T')[0] === dayKey;
        }).length;
        data.push({
          label: date.toLocaleDateString('en-US', { weekday: 'short' }),
          count
        });
      }
      return data;
    } else if (timeRange === 'month') {
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const requirementsByDay: Record<string, number> = {};

      requirements.forEach(req => {
        const date = new Date(req.created_at);
        if (date >= firstDayOfMonth && date <= today) {
          const dayKey = date.toISOString().split('T')[0];
          requirementsByDay[dayKey] = (requirementsByDay[dayKey] || 0) + 1;
        }
      });

      return Object.entries(requirementsByDay)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([day, count]) => ({
          label: new Date(day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          count
        }));
    } else if (timeRange === 'quarter') {
      const currentMonth = today.getMonth();
      const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
      const quarterStart = new Date(today.getFullYear(), quarterStartMonth, 1);

      const monthlyData: Record<string, number> = {};

      requirements.forEach(req => {
        const date = new Date(req.created_at);
        if (date >= quarterStart && date <= today) {
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          monthlyData[monthKey] = (monthlyData[monthKey] || 0) + 1;
        }
      });

      const data: { label: string; count: number }[] = [];
      for (let i = 0; i < 3; i++) {
        const month = new Date(today.getFullYear(), quarterStartMonth + i, 1);
        const monthKey = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`;
        data.push({
          label: month.toLocaleDateString('en-US', { month: 'short' }),
          count: monthlyData[monthKey] || 0
        });
      }
      return data;
    } else {
      const yearStart = new Date(today.getFullYear(), 0, 1);
      const monthlyData: Record<string, number> = {};

      requirements.forEach(req => {
        const date = new Date(req.created_at);
        if (date >= yearStart && date <= today) {
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          monthlyData[monthKey] = (monthlyData[monthKey] || 0) + 1;
        }
      });

      const data: { label: string; count: number }[] = [];
      for (let i = 0; i < 12; i++) {
        const month = new Date(today.getFullYear(), i, 1);
        const monthKey = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`;
        data.push({
          label: month.toLocaleDateString('en-US', { month: 'short' }),
          count: monthlyData[monthKey] || 0
        });
      }
      return data;
    }
  };

  const timelineData = getTimelineData();
  const maxDayCount = Math.max(...timelineData.map(t => t.count), 1);

  // Active requirements with comments
  const requirementsWithComments = requirements.filter(r => (r.comment_count || 0) > 0).length;
  const engagementRate = totalRequirements > 0 ? ((requirementsWithComments / totalRequirements) * 100).toFixed(0) : '0';

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
            <p className="text-gray-600 font-medium">Loading analytics...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="relative bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-500 rounded-2xl shadow-xl mb-8 p-8 overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItaDJ2LTJoLTJ6bTAgNHYyaDJ2LTJoLTJ6bS0yIDJ2Mmgydi0yaC0yem0wLTR2Mmgydi0yaC0yem0yLTJ2LTJoLTJ2Mmgyem0tMiAwdi0yaC0ydjJoMnptLTItMnYtMmgtMnYyaDJ6bTQgMHYtMmgtMnYyaDJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>

          <div className="relative">
            <button
              onClick={() => navigate(`/projects/${projectId}/requirements`)}
              className="inline-flex items-center gap-2 px-4 py-2 mb-4 text-sm font-medium text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full transition-all border border-white/20 hover:border-white/30"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Requirements
            </button>

            <div className="flex items-end justify-between">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">Requirements Analytics</h1>
                <p className="text-blue-100 text-lg">Comprehensive insights and performance metrics</p>
              </div>
              <div className="hidden md:block">
                <div className="flex items-center gap-2 px-5 py-3 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span className="text-white font-semibold">{totalRequirements}</span>
                  <span className="text-blue-100 text-sm">Total</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="group bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 p-6 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Total Requirements</p>
                <p className="text-4xl font-bold text-gray-900 mb-1">{totalRequirements}</p>
                <p className="text-xs text-gray-500">All project requirements</p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-slate-500 to-slate-700 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="group bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 p-6 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Pending Approval</p>
                <p className="text-4xl font-bold text-amber-600 mb-1">{byStatus.pending || 0}</p>
                <p className="text-xs text-gray-500">Awaiting review</p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="group bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 p-6 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Approved</p>
                <p className="text-4xl font-bold text-emerald-600 mb-1">{byStatus.approved || 0}</p>
                <p className="text-xs text-gray-500">Ready to implement</p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="group bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 p-6 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Engagement Rate</p>
                <p className="text-4xl font-bold text-blue-600 mb-1">{engagementRate}%</p>
                <p className="text-xs text-gray-500">Requirements with comments</p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Main Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Requirements Over Time */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Requirements Created</h2>
                <p className="text-sm text-gray-500 mt-1">Track creation over time</p>
              </div>
              <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
                {(['week', 'month', 'quarter', 'year'] as const).map((range) => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all ${
                      timeRange === range
                        ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {range.charAt(0).toUpperCase() + range.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {timelineData.length > 0 ? (
              <div className="mt-6">
                <div className="h-72 flex items-end justify-between gap-3 px-2">
                  {timelineData.map((item, index) => {
                    const heightPercent = maxDayCount > 0 ? (item.count / maxDayCount) * 100 : 0;
                    return (
                      <div key={index} className="flex-1 flex flex-col items-center gap-3">
                        <div className="w-full relative group">
                          <div
                            className="w-full bg-gradient-to-t from-indigo-600 to-blue-500 rounded-t-lg transition-all hover:from-indigo-700 hover:to-blue-600 shadow-sm hover:shadow-md"
                            style={{
                              height: `${Math.max(heightPercent * 2.5, item.count > 0 ? 8 : 0)}px`,
                            }}
                          />
                          {item.count > 0 && (
                            <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <span className="text-xs font-bold text-white bg-gray-900 px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap">
                                {item.count} {item.count === 1 ? 'req' : 'reqs'}
                              </span>
                            </div>
                          )}
                        </div>
                        <span className="text-xs text-gray-600 font-semibold">{item.label}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 pt-5 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gradient-to-r from-indigo-600 to-blue-500"></div>
                      <span className="text-sm text-gray-600">
                        <span className="font-bold text-gray-900">{timelineData.reduce((sum, item) => sum + item.count, 0)}</span> requirements added
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 font-medium">
                      {timeRange === 'week' ? 'Last 7 days' : timeRange === 'month' ? 'This month' : timeRange === 'quarter' ? 'This quarter' : 'This year'}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-72 text-gray-400">
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p className="text-sm font-medium">No timeline data</p>
                  <p className="text-xs text-gray-400 mt-1">Requirements will appear here once created</p>
                </div>
              </div>
            )}
          </div>

          {/* Requirements by Priority */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900">Priority Distribution</h2>
              <p className="text-sm text-gray-500 mt-1">Requirements by priority level</p>
            </div>
            <div className="space-y-5">
              {(['critical', 'high', 'medium', 'low'] as const).map((priority) => {
                const count = byPriority[priority] || 0;
                const percentage = totalRequirements > 0 ? (count / totalRequirements) * 100 : 0;
                const colors = {
                  critical: { gradient: 'from-rose-500 to-rose-600', text: 'text-rose-700', bg: 'bg-rose-100' },
                  high: { gradient: 'from-orange-500 to-orange-600', text: 'text-orange-700', bg: 'bg-orange-100' },
                  medium: { gradient: 'from-amber-500 to-amber-600', text: 'text-amber-700', bg: 'bg-amber-100' },
                  low: { gradient: 'from-slate-400 to-slate-500', text: 'text-slate-700', bg: 'bg-slate-100' }
                };
                return (
                  <div key={priority} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full bg-gradient-to-r ${colors[priority].gradient} shadow-sm`} />
                        <span className="text-sm font-semibold text-gray-700 capitalize">{priority}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-gray-900">{count}</span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${colors[priority].bg} ${colors[priority].text}`}>
                          {percentage.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                      <div
                        className={`bg-gradient-to-r ${colors[priority].gradient} h-full transition-all duration-700 ease-out rounded-full`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Secondary Data Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Contributors */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900">Top Contributors</h2>
              <p className="text-sm text-gray-500 mt-1">Most active team members</p>
            </div>
            {userStats.length > 0 ? (
              <div className="space-y-4">
                {userStats.map((user, index) => {
                  const percentage = totalRequirements > 0 ? (user.count / totalRequirements) * 100 : 0;
                  const isTopThree = index < 3;
                  return (
                    <div key={user.email} className="group">
                      <div className="flex items-center gap-4 mb-2">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                          isTopThree
                            ? 'bg-gradient-to-br from-indigo-600 to-blue-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-semibold text-gray-900 truncate">{user.name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-gray-900">{user.count}</span>
                              {isTopThree && (
                                <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              )}
                            </div>
                          </div>
                          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                            <div
                              className={`h-full ${isTopThree ? 'bg-gradient-to-r from-indigo-600 to-blue-600' : 'bg-slate-500'} transition-all duration-700 ease-out rounded-full`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-400">
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <p className="text-sm font-medium">No contributor data</p>
                </div>
              </div>
            )}
          </div>

          {/* Most Discussed */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900">Most Discussed</h2>
              <p className="text-sm text-gray-500 mt-1">Top requirements by engagement</p>
            </div>
            {mostDiscussed.length > 0 && mostDiscussed.some(req => ((req.comment_count || 0) + (req.reply_count || 0)) > 0) ? (
              <div className="space-y-2">
                {mostDiscussed.slice(0, 5).map((req, index) => {
                  const totalDiscussion = (req.comment_count || 0) + (req.reply_count || 0);
                  if (totalDiscussion === 0) return null;
                  return (
                    <div
                      key={req.id}
                      className="group flex items-center gap-4 p-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 cursor-pointer transition-all border border-transparent hover:border-indigo-200 hover:shadow-md"
                      onClick={() => navigate(`/projects/${projectId}/requirements`)}
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm shadow-md">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-indigo-700 transition-colors">{req.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            <span className="text-xs text-gray-600 font-medium">
                              {totalDiscussion}
                            </span>
                          </div>
                          <span className="text-xs text-gray-300">•</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                            req.priority === 'critical' ? 'bg-rose-100 text-rose-700' :
                            req.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                            req.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {req.priority}
                          </span>
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-400">
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p className="text-sm font-medium">No discussions yet</p>
                  <p className="text-xs text-gray-400 mt-1">Start commenting on requirements</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Detailed Statistics */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Detailed Statistics</h2>
            <p className="text-sm text-gray-500 mt-1">Comprehensive breakdown by category</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* By Type */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">By Type</h3>
              </div>
              <div className="space-y-3">
                {Object.entries(byType)
                  .sort(([, a], [, b]) => b - a)
                  .map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <span className="text-sm text-gray-700 font-medium capitalize">{type.replace('-', ' ')}</span>
                      <span className="text-sm font-bold text-gray-900 px-2.5 py-1 rounded-full bg-gray-100">{count}</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* By Status */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">By Status</h3>
              </div>
              <div className="space-y-3">
                {Object.entries(byStatus)
                  .sort(([, a], [, b]) => b - a)
                  .map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <span className="text-sm text-gray-700 font-medium capitalize">{status}</span>
                      <span className="text-sm font-bold text-gray-900 px-2.5 py-1 rounded-full bg-gray-100">{count}</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Engagement Metrics */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                  </svg>
                </div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Engagement</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <span className="text-sm text-gray-700 font-medium">With Comments</span>
                  <span className="text-sm font-bold text-gray-900 px-2.5 py-1 rounded-full bg-gray-100">{requirementsWithComments}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <span className="text-sm text-gray-700 font-medium">Without Comments</span>
                  <span className="text-sm font-bold text-gray-900 px-2.5 py-1 rounded-full bg-gray-100">{totalRequirements - requirementsWithComments}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <span className="text-sm text-gray-700 font-medium">Avg. Comments</span>
                  <span className="text-sm font-bold text-gray-900 px-2.5 py-1 rounded-full bg-gray-100">
                    {totalRequirements > 0
                      ? (requirements.reduce((sum, r) => sum + (r.comment_count || 0) + (r.reply_count || 0), 0) / totalRequirements).toFixed(1)
                      : '0'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
